import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "./firebase"; // adjust path

const LANGS = ["sq", "en"];

// simple slug generator for new specialties (admin only)
function slugify(input) {
  return (input || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/ë/g, "e")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AdminSpecialtyFixPage() {
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]); // array of docs
  const [specialtiesById, setSpecialtiesById] = useState({});
  const [search, setSearch] = useState("");
  const [bulkSpecialtyId, setBulkSpecialtyId] = useState("dermatology");
  const [bulkFilterRaw, setBulkFilterRaw] = useState("Dermatologji");
  const [status, setStatus] = useState("");

  async function loadAll() {
    setLoading(true);
    setStatus("");
    try {
      const [docSnap, specSnap] = await Promise.all([
        getDocs(collection(db, "doctors")),
        getDocs(collection(db, "specialties")),
      ]);

      const docs = docSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const specs = specSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const map = {};
      for (const s of specs) map[s.id] = s;

      setDoctors(docs);
      setSpecialties(specs);
      setSpecialtiesById(map);
    } catch (e) {
      console.error(e);
      setStatus("❌ Failed to load data. Check console + rules.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  const filteredDoctors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter((d) => {
      const name = (d.fullName || d.name || "").toLowerCase();
      const raw = (d.specialtyRawSq || d.speciality || d.specialty || "").toLowerCase();
      const sid = (d.specialtyId || "").toLowerCase();
      return name.includes(q) || raw.includes(q) || sid.includes(q);
    });
  }, [doctors, search]);

  async function createSpecialtyIfMissing({ sqLabel, enLabel }) {
    const id = slugify(enLabel || sqLabel);
    if (!id) throw new Error("Missing labels for specialty");

    // create doc in specialties
    await setDoc(
      doc(db, "specialties", id),
      {
        id,
        labels: { sq: sqLabel || "", en: enLabel || "" },
        synonyms: { sq: [], en: [] },
        isActive: true,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setStatus(`✅ Specialty ensured: ${id}`);
    await loadAll();
    return id;
  }

  async function saveOneDoctorSpecialty(doctorId, specialtyId, specialtyRawSq) {
    setStatus("");
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "doctors", doctorId), {
        specialtyId: specialtyId || null,
        specialtyRawSq: specialtyRawSq || null,
        updatedAt: serverTimestamp(),
      });

      // optional log
      batch.set(doc(db, "adminFixLogs", `${doctorId}_${Date.now()}`), {
        type: "doctor_specialty_fix",
        doctorId,
        specialtyId: specialtyId || null,
        specialtyRawSq: specialtyRawSq || null,
        createdAt: serverTimestamp(),
      });

      await batch.commit();
      setDoctors((prev) =>
        prev.map((d) =>
          d.id === doctorId
            ? { ...d, specialtyId: specialtyId || null, specialtyRawSq: specialtyRawSq || null }
            : d
        )
      );
      setStatus("✅ Saved doctor specialty");
    } catch (e) {
      console.error(e);
      setStatus("❌ Save failed. Check rules / console.");
    }
  }

  async function bulkApply() {
    // Apply bulkSpecialtyId to doctors whose raw specialty matches bulkFilterRaw
    const rawMatch = bulkFilterRaw.trim().toLowerCase();
    if (!rawMatch) {
      setStatus("❌ Put a raw Albanian specialty filter first.");
      return;
    }

    const targets = doctors.filter((d) => {
      const raw = (d.specialtyRawSq || d.speciality || d.specialty || "").toLowerCase().trim();
      return raw === rawMatch && d.specialtyId !== bulkSpecialtyId;
    });

    if (targets.length === 0) {
      setStatus("ℹ️ No matching doctors found for bulk update.");
      return;
    }

    setStatus(`⏳ Bulk updating ${targets.length} doctors...`);
    try {
      const batch = writeBatch(db);

      targets.forEach((d) => {
        batch.update(doc(db, "doctors", d.id), {
          specialtyId: bulkSpecialtyId,
          specialtyRawSq: d.specialtyRawSq || d.speciality || d.specialty || null,
          updatedAt: serverTimestamp(),
        });
      });

      batch.set(doc(db, "adminFixLogs", `bulk_${Date.now()}`), {
        type: "bulk_doctor_specialty_fix",
        filterRaw: bulkFilterRaw,
        appliedSpecialtyId: bulkSpecialtyId,
        count: targets.length,
        createdAt: serverTimestamp(),
      });

      await batch.commit();

      setDoctors((prev) =>
        prev.map((d) => {
          const raw = (d.specialtyRawSq || d.speciality || d.specialty || "").toLowerCase().trim();
          if (raw === rawMatch) return { ...d, specialtyId: bulkSpecialtyId };
          return d;
        })
      );

      setStatus(`✅ Bulk updated ${targets.length} doctors.`);
    } catch (e) {
      console.error(e);
      setStatus("❌ Bulk update failed. Check rules / console.");
    }
  }

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 8 }}>Admin • Specialty Fix</h2>
      <p style={{ opacity: 0.8, marginTop: 0 }}>
        Standardize doctor specialties by mapping Albanian raw values to a canonical <code>specialtyId</code>.
      </p>

      {status ? (
        <div style={{ padding: 12, border: "1px solid #ddd", borderRadius: 10, marginBottom: 12 }}>
          {status}
        </div>
      ) : null}

      {/* Bulk tools */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Bulk filter (raw Albanian text)</div>
          <input
            value={bulkFilterRaw}
            onChange={(e) => setBulkFilterRaw(e.target.value)}
            placeholder="e.g. Dermatologji"
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Apply specialtyId</div>
          <select
            value={bulkSpecialtyId}
            onChange={(e) => setBulkSpecialtyId(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
          >
            {specialties
              .filter((s) => s.isActive !== false)
              .sort((a, b) => (a.labels?.en || a.id).localeCompare(b.labels?.en || b.id))
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.labels?.en || s.id} • ({s.labels?.sq || "—"})
                </option>
              ))}
          </select>
        </div>

        <div style={{ alignSelf: "flex-end" }}>
          <button
            onClick={bulkApply}
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid #111",
              background: "#111",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Bulk Apply
          </button>
        </div>
      </div>

      {/* Create specialty */}
      <CreateSpecialtyCard onCreate={createSpecialtyIfMissing} />

      {/* Search */}
      <div style={{ margin: "16px 0 10px" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search doctors by name / raw specialty / specialtyId…"
          style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
        />
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#fafafa" }}>
              <th style={th}>Doctor</th>
              <th style={th}>Raw (sq)</th>
              <th style={th}>specialtyId</th>
              <th style={th}>Set</th>
              <th style={th}>Save</th>
            </tr>
          </thead>
          <tbody>
            {filteredDoctors.map((d) => {
              const raw = d.specialtyRawSq || d.speciality || d.specialty || "";
              const currentId = d.specialtyId || "";
              return (
                <DoctorRow
                  key={d.id}
                  doctor={d}
                  raw={raw}
                  currentId={currentId}
                  specialties={specialties}
                  onSave={saveOneDoctorSpecialty}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, opacity: 0.7, fontSize: 12 }}>
        Tip: once all doctors have <code>specialtyId</code>, your UI should ignore raw strings and always render from{" "}
        <code>specialties/{`{specialtyId}`}</code> labels.
      </div>
    </div>
  );
}

function CreateSpecialtyCard({ onCreate }) {
  const [sq, setSq] = useState("Dermatologji");
  const [en, setEn] = useState("Dermatology");

  return (
    <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 12, marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Create/Ensure Specialty</div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <input
          value={sq}
          onChange={(e) => setSq(e.target.value)}
          placeholder="sq label"
          style={{ flex: 1, minWidth: 200, padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
        />
        <input
          value={en}
          onChange={(e) => setEn(e.target.value)}
          placeholder="en label"
          style={{ flex: 1, minWidth: 200, padding: 10, borderRadius: 10, border: "1px solid #ddd" }}
        />
        <button
          onClick={() => onCreate({ sqLabel: sq, enLabel: en })}
          style={{ padding: "10px 14px", borderRadius: 12, border: "1px solid #111", background: "#fff" }}
        >
          Ensure
        </button>
      </div>
    </div>
  );
}

function DoctorRow({ doctor, raw, currentId, specialties, onSave }) {
  const [selected, setSelected] = useState(currentId);

  useEffect(() => setSelected(currentId), [currentId]);

  return (
    <tr>
      <td style={td}>
        <div style={{ fontWeight: 600 }}>{doctor.fullName || doctor.name || "—"}</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>{doctor.id}</div>
      </td>
      <td style={td}>{raw || "—"}</td>
      <td style={td}>
        <code>{currentId || "—"}</code>
      </td>
      <td style={td}>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          style={{ width: "100%", padding: 8, borderRadius: 10, border: "1px solid #ddd" }}
        >
          <option value="">— none —</option>
          {specialties
            .filter((s) => s.isActive !== false)
            .sort((a, b) => (a.labels?.en || a.id).localeCompare(b.labels?.en || b.id))
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.labels?.en || s.id} • ({s.labels?.sq || "—"})
              </option>
            ))}
        </select>
      </td>
      <td style={td}>
        <button
          onClick={() => onSave(doctor.id, selected, raw)}
          style={{ padding: "8px 12px", borderRadius: 12, border: "1px solid #111", background: "#111", color: "#fff" }}
        >
          Save
        </button>
      </td>
    </tr>
  );
}

const th = { textAlign: "left", padding: 10, borderBottom: "1px solid #eee", fontSize: 12, opacity: 0.8 };
const td = { padding: 10, borderBottom: "1px solid #f2f2f2", verticalAlign: "top" };
